// frontend/src/TenancyAdminDashboard.tsx - Fixed Tenant Administration Interface

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import HelpTooltip from './components/HelpTooltip';
import { API_URL } from './apiConfig';

interface TenantBranding {
  id: number;
  logo_url?: string;
  logo_filename?: string;
  logo_upload_date?: string;
}

interface OrganizationalLevel {
  id: number;
  level_order: number;
  level_name: string;
  is_active: boolean;
}

interface OrganizationalUnit {
  id: number;
  parent_id?: number;
  name: string;
  level_name: string;
  level_order: number;
  description?: string;
  is_active: boolean;
  children?: OrganizationalUnit[];
}

interface TenantUser {
  id: number;
  username: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  organizational_unit_id?: number;
  organizational_unit_name?: string;
  created_at: string;
  last_login_at?: string;
}

interface TenantStats {
  total_users: number;
  active_users: number;
  total_processes: number;
  active_processes: number;
  total_organizational_units: number;
  recent_logins: number;
}

interface TenancyAdminDashboardProps {
  onBack: () => void;
}

const TenancyAdminDashboard: React.FC<TenancyAdminDashboardProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'branding' | 'structure' | 'users'>('overview');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  
  // Branding state
  const [tenantBranding, setTenantBranding] = useState<TenantBranding | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  
  // Organizational structure state
  const [organizationalLevels, setOrganizationalLevels] = useState<OrganizationalLevel[]>([]);
  const [organizationalUnits, setOrganizationalUnits] = useState<OrganizationalUnit[]>([]);
  const [newLevelName, setNewLevelName] = useState('');
  const [showCreateUnitForm, setShowCreateUnitForm] = useState(false);
  const [newUnitData, setNewUnitData] = useState({
    name: '',
    level_id: '',
    parent_id: '',
    description: ''
  });
  
  // User management state
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [showCreateUserForm, setShowCreateUserForm] = useState(false);
  
  // Overview/stats state
  const [tenantStats, setTenantStats] = useState<TenantStats | null>(null);
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'EVALUATION_PANEL_MEMBER',
    organizational_unit_id: ''
  });
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<TenantUser | null>(null);

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  // Load tenant data
  useEffect(() => {
    loadTenantData();
  }, []);

  const loadTenantData = async () => {
    setLoading(true);
    try {
      const token = Cookies.get('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load tenant branding
      const brandingResponse = await axios.get(`${API_URL}/api/tenants/branding`, { headers });
      setTenantBranding(brandingResponse.data);
      
      // Load organizational structure
      const levelsResponse = await axios.get(`${API_URL}/api/tenants/organizational-levels`, { headers });
      setOrganizationalLevels(levelsResponse.data);
      
      const unitsResponse = await axios.get(`${API_URL}/api/tenants/organizational-units`, { headers });
      setOrganizationalUnits(unitsResponse.data);
      
      // Load tenant users
      const usersResponse = await axios.get(`${API_URL}/api/tenants/users`, { headers });
      setTenantUsers(usersResponse.data);
      
      // Load processes for stats
      const processesResponse = await axios.get(`${API_URL}/api/processes/`, { headers });
      const processes = processesResponse.data;
      
      // Calculate tenant statistics
      const stats: TenantStats = {
        total_users: usersResponse.data.length,
        active_users: usersResponse.data.filter((user: TenantUser) => user.is_active).length,
        total_processes: processes.length,
        active_processes: processes.filter((process: any) => process.status !== 'CLOSED').length,
        total_organizational_units: unitsResponse.data.length,
        recent_logins: usersResponse.data.filter((user: TenantUser) => {
          if (!user.last_login_at) return false;
          const oneWeekAgo = new Date();
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          return new Date(user.last_login_at) > oneWeekAgo;
        }).length
      };
      setTenantStats(stats);
    } catch (error) {
      console.error('Error loading tenant data:', error);
      showNotification('error', 'Failed to load tenant configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'].includes(file.type)) {
        showNotification('error', 'Please select a valid image file (JPG, PNG, SVG, or WebP)');
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        showNotification('error', 'Logo file must be smaller than 2MB');
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      
      const token = Cookies.get('access_token');
      const response = await axios.post(`${API_URL}/api/tenants/upload-logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setTenantBranding(response.data);
      setLogoFile(null);
      setLogoPreview(null);
      showNotification('success', 'Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      showNotification('error', 'Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  const addOrganizationalLevel = async () => {
    if (!newLevelName.trim()) return;
    
    setLoading(true);
    try {
      const token = Cookies.get('access_token');
      const response = await axios.post(`${API_URL}/api/tenants/organizational-levels`, 
        { level_name: newLevelName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOrganizationalLevels([...organizationalLevels, response.data]);
      setNewLevelName('');
      showNotification('success', `Added organizational level: ${newLevelName}`);
    } catch (error: any) {
      console.error('Error adding organizational level:', error);
      const message = error.response?.data?.detail || 'Failed to add organizational level';
      showNotification('error', message);
    } finally {
      setLoading(false);
    }
  };

  const createOrganizationalUnit = async () => {
    if (!newUnitData.name.trim() || !newUnitData.level_id) {
      showNotification('error', 'Please fill in the unit name and select a level');
      return;
    }
    
    setLoading(true);
    try {
      const token = Cookies.get('access_token');
      const unitData = {
        name: newUnitData.name.trim(),
        level_id: parseInt(newUnitData.level_id),
        parent_id: newUnitData.parent_id ? parseInt(newUnitData.parent_id) : null,
        description: newUnitData.description.trim() || null
      };
      
      const response = await axios.post(`${API_URL}/api/tenants/organizational-units`, unitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOrganizationalUnits([...organizationalUnits, response.data]);
      setNewUnitData({
        name: '',
        level_id: '',
        parent_id: '',
        description: ''
      });
      setShowCreateUnitForm(false);
      showNotification('success', `Created organisational unit: ${newUnitData.name}`);
    } catch (error: any) {
      console.error('Error creating organizational unit:', error);
      const message = error.response?.data?.detail || 'Failed to create organisational unit';
      showNotification('error', message);
    } finally {
      setLoading(false);
    }
  };

  const createTenantUser = async () => {
    if (!newUserData.username.trim() || !newUserData.email.trim() || !newUserData.full_name.trim() || !newUserData.password.trim()) {
      showNotification('error', 'Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const token = Cookies.get('access_token');
      const userData = {
        username: newUserData.username.trim(),
        email: newUserData.email.trim(),
        full_name: newUserData.full_name.trim(),
        password: newUserData.password,
        role: newUserData.role,
        organizational_unit_id: newUserData.organizational_unit_id ? parseInt(newUserData.organizational_unit_id) : null
      };
      
      const response = await axios.post(`${API_URL}/api/tenants/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTenantUsers([...tenantUsers, response.data]);
      setNewUserData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        role: 'EVALUATION_PANEL_MEMBER',
        organizational_unit_id: ''
      });
      setShowCreateUserForm(false);
      showNotification('success', `User ${newUserData.username} created successfully`);
    } catch (error: any) {
      console.error('Error creating user:', error);
      const message = error.response?.data?.detail || 'Failed to create user';
      showNotification('error', message);
    } finally {
      setLoading(false);
    }
  };

  const updateTenantUser = async (userId: number, updateData: any) => {
    setLoading(true);
    try {
      const token = Cookies.get('access_token');
      const response = await axios.put(`${API_URL}/api/tenants/users/${userId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTenantUsers(tenantUsers.map(user => user.id === userId ? response.data : user));
      setSelectedUserForEdit(null);
      showNotification('success', 'User updated successfully');
    } catch (error: any) {
      console.error('Error updating user:', error);
      const message = error.response?.data?.detail || 'Failed to update user';
      showNotification('error', message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: 'var(--spacing-lg)'
    },
    logoPreview: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      padding: '1rem',
      border: '2px dashed #d1d5db',
      borderRadius: '8px',
      backgroundColor: '#f9fafb'
    },
    logoImage: {
      maxWidth: '200px',
      maxHeight: '100px',
      objectFit: 'contain' as const
    }
  };
  
  const renderOverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
      {/* Active Users Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">👥 Users</h3>
        </div>
        <div className="card-body">
          <div className="stat-number">{tenantStats?.active_users || 0} / {tenantStats?.total_users || 0}</div>
          <div className="stat-label">Active / Total Users</div>
        </div>
      </div>

      {/* Active Processes Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📋 Processes</h3>
        </div>
        <div className="card-body">
          <div className="stat-number">{tenantStats?.active_processes || 0} / {tenantStats?.total_processes || 0}</div>
          <div className="stat-label">Active / Total Processes</div>
        </div>
      </div>

      {/* Organizational Units Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">🏢 Organisation Units</h3>
        </div>
        <div className="card-body">
          <div className="stat-number">{tenantStats?.total_organizational_units || 0}</div>
          <div className="stat-label">Total Units</div>
        </div>
      </div>

      {/* Recent Logins Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">🔐 Recent Activity</h3>
        </div>
        <div className="card-body">
          <div className="stat-number">{tenantStats?.recent_logins || 0}</div>
          <div className="stat-label">Logins (Last 7 days)</div>
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="card md:col-span-2">
        <div className="card-header">
          <h3 className="card-title">⚡ Quick Actions</h3>
        </div>
        <div className="card-body">
          <div className="flex flex-wrap gap-sm">
            <button 
              className="btn btn-primary btn-sm"
              onClick={() => setActiveTab('users')}
            >
              👥 Manage Users
            </button>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => setActiveTab('structure')}
            >
              🏢 Organisation Structure
            </button>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => setActiveTab('branding')}
            >
              🎨 Update Branding
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBrandingTab = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">
          Organisation Logo
          <HelpTooltip 
            content="Upload your organisation's logo to display alongside the EvaluTool logo in the header. Recommended: PNG or SVG format, max 2MB, optimal size 300x150px for best quality." 
            position="right"
            maxWidth="350px"
          />
        </h2>
      </div>
      
      <div className="card-content">
        <div className="form-group">
          <label className="form-label">
            Current Logo
            <HelpTooltip 
              content="This is your currently active logo. It appears in the application header next to the EvaluTool logo." 
            />
          </label>
          {tenantBranding?.logo_url ? (
            <div style={styles.logoPreview}>
              <img 
                src={tenantBranding.logo_url} 
                alt="Current Organisation Logo" 
                style={styles.logoImage}
              />
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>
                Uploaded: {tenantBranding.logo_upload_date ? new Date(tenantBranding.logo_upload_date).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          ) : (
            <div style={styles.logoPreview}>
              <p style={{ color: '#64748b' }}>No logo uploaded yet</p>
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">
            Upload New Logo
            <HelpTooltip 
              content="Accepted formats: JPG, PNG, SVG, WebP. Maximum file size: 2MB. For best results, use a logo with transparent background (PNG/SVG) and dimensions around 300x150 pixels." 
            />
          </label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/svg+xml,image/webp"
            onChange={handleLogoChange}
            className="input"
          />
        </div>

        {logoPreview && (
          <div style={styles.logoPreview}>
            <img src={logoPreview} alt="Logo Preview" style={styles.logoImage} />
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#64748b' }}>Preview</p>
            <button 
              onClick={uploadLogo}
              disabled={loading}
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
            >
              {loading ? 'Uploading...' : 'Upload Logo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderStructureTab = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">
          Organisational Levels
          <HelpTooltip 
            content="Define the hierarchical levels for your organisation (e.g., Directorate → Division → Branch). These levels will be used to organise users and processes within your tenancy." 
            position="right"
            maxWidth="350px"
          />
        </h2>
      </div>
      
      <div className="card-content">
        <div className="form-group">
          <label className="form-label">
            Current Organisational Structure
            <HelpTooltip 
              content="These are your defined organisational levels in hierarchical order. Level 1 is the highest level (e.g., Directorate), Level 2 is the second level (e.g., Division), and so on." 
            />
          </label>
          <div>
            {organizationalLevels.map((level) => (
              <div key={level.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                padding: '0.75rem', 
                marginBottom: '0.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px' 
              }}>
                <span style={{ 
                  backgroundColor: '#3b82f6', 
                  color: 'white', 
                  padding: '0.25rem 0.75rem', 
                  borderRadius: '12px', 
                  fontSize: '0.8rem', 
                  fontWeight: '600' 
                }}>
                  Level {level.level_order}
                </span>
                <span style={{ fontWeight: '600', marginLeft: '1rem' }}>{level.level_name}</span>
                {!level.is_active && (
                  <span style={{ marginLeft: 'auto', color: '#ef4444', fontSize: '0.9rem' }}>Inactive</span>
                )}
              </div>
            ))}
            {organizationalLevels.length === 0 && (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                No organisational levels defined yet
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Add New Level
            <HelpTooltip 
              content="Add a new organisational level (e.g., 'Directorate', 'Division', 'Branch', 'Team'). New levels are added at the bottom of the hierarchy." 
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
            <input
              type="text"
              value={newLevelName}
              onChange={(e) => setNewLevelName(e.target.value)}
              placeholder="e.g., Directorate, Division, Branch..."
              className="input"
              style={{ flex: 1 }}
            />
            <button
              onClick={addOrganizationalLevel}
              disabled={loading || !newLevelName.trim()}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              {loading ? 'Adding...' : 'Add Level'}
            </button>
          </div>
        </div>

        <div className="form-group">
          <div className="flex justify-between items-center mb-md">
            <label className="form-label">
              Organisational Units
              <HelpTooltip 
                content="Create and manage the actual organisational units within your defined levels (e.g., 'IT Directorate', 'Finance Division'). Users and processes will be assigned to these units." 
                position="right"
                maxWidth="350px"
              />
            </label>
            <button
              onClick={() => setShowCreateUnitForm(!showCreateUnitForm)}
              disabled={organizationalLevels.length === 0}
              className="btn btn-primary btn-sm"
              title={organizationalLevels.length === 0 ? "Create organisational levels first" : ""}
            >
              {showCreateUnitForm ? 'Cancel' : '+ Add Unit'}
            </button>
          </div>

          {showCreateUnitForm && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              marginBottom: '1.5rem',
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: '600' }}>Create New Organisational Unit</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label className="form-label">Unit Name *</label>
                  <input
                    type="text"
                    value={newUnitData.name}
                    onChange={(e) => setNewUnitData({...newUnitData, name: e.target.value})}
                    placeholder="e.g., IT Directorate, Finance Division"
                    className="input"
                  />
                </div>
                <div>
                  <label className="form-label">Organisational Level *</label>
                  <select
                    value={newUnitData.level_id}
                    onChange={(e) => setNewUnitData({...newUnitData, level_id: e.target.value})}
                    className="input"
                  >
                    <option value="">Select level</option>
                    {organizationalLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        Level {level.level_order} - {level.level_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Parent Unit (Optional)</label>
                  <select
                    value={newUnitData.parent_id}
                    onChange={(e) => setNewUnitData({...newUnitData, parent_id: e.target.value})}
                    className="input"
                  >
                    <option value="">No parent (top level)</option>
                    {organizationalUnits
                      .filter(unit => {
                        if (!newUnitData.level_id) return true;
                        const selectedLevel = organizationalLevels.find(l => l.id.toString() === newUnitData.level_id);
                        return selectedLevel ? unit.level_order < selectedLevel.level_order : true;
                      })
                      .map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name} ({unit.level_name})
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Description (Optional)</label>
                  <input
                    type="text"
                    value={newUnitData.description}
                    onChange={(e) => setNewUnitData({...newUnitData, description: e.target.value})}
                    placeholder="Brief description of this unit"
                    className="input"
                  />
                </div>
              </div>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={createOrganizationalUnit}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Creating...' : 'Create Unit'}
                </button>
                <button
                  onClick={() => setShowCreateUnitForm(false)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div>
            {organizationalUnits.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                No organisational units created yet
              </div>
            ) : (
              <div>
                {organizationalUnits.map((unit) => (
                  <div key={unit.id} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center', 
                    padding: '1rem', 
                    marginBottom: '0.5rem',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '600', fontSize: '1rem' }}>{unit.name}</span>
                        <span style={{ 
                          backgroundColor: '#3b82f6', 
                          color: 'white', 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '12px', 
                          fontSize: '0.75rem', 
                          fontWeight: '600' 
                        }}>
                          {unit.level_name}
                        </span>
                      </div>
                      {unit.description && (
                        <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>
                          {unit.description}
                        </p>
                      )}
                      {unit.parent_id && (
                        <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '0.8rem' }}>
                          Under: {organizationalUnits.find(u => u.id === unit.parent_id)?.name}
                        </p>
                      )}
                    </div>
                    <div>
                      {!unit.is_active && (
                        <span style={{ color: '#ef4444', fontSize: '0.9rem' }}>Inactive</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsersTab = () => (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">
          User Management
          <HelpTooltip 
            content="Manage users within your tenancy and assign them to organisational units. Control user access and permissions across your organisation." 
            position="right"
            maxWidth="350px"
          />
        </h2>
      </div>
      <div className="card-content">
        {/* Create User Section */}
        <div className="form-group">
          <div className="flex justify-between items-center mb-md">
            <label className="form-label">
              Organisation Users
              <HelpTooltip 
                content="All users within your organisation. You can create new users, assign them to organisational units, and manage their roles and permissions." 
              />
            </label>
            <button
              onClick={() => setShowCreateUserForm(!showCreateUserForm)}
              className="btn btn-primary btn-sm"
            >
              {showCreateUserForm ? 'Cancel' : '+ Add User'}
            </button>
          </div>

          {showCreateUserForm && (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              marginBottom: '1.5rem',
              border: '1px solid #e9ecef'
            }}>
              <h3 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>Create New User</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label className="form-label">Username *</label>
                  <input
                    type="text"
                    value={newUserData.username}
                    onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                    placeholder="Enter username"
                    className="input"
                  />
                </div>
                <div>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                    placeholder="Enter email address"
                    className="input"
                  />
                </div>
                <div>
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    value={newUserData.full_name}
                    onChange={(e) => setNewUserData({...newUserData, full_name: e.target.value})}
                    placeholder="Enter full name"
                    className="input"
                  />
                </div>
                <div>
                  <label className="form-label">Initial Password *</label>
                  <input
                    type="password"
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                    placeholder="Enter temporary password"
                    className="input"
                  />
                </div>
                <div>
                  <label className="form-label">Role</label>
                  <select
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
                    className="input"
                  >
                    <option value="EVALUATION_PANEL_MEMBER">Evaluation Panel Member</option>
                    <option value="PROCESS_OWNER">Process Owner</option>
                    <option value="ADMIN">Administrator</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Organisational Unit</label>
                  <select
                    value={newUserData.organizational_unit_id}
                    onChange={(e) => setNewUserData({...newUserData, organizational_unit_id: e.target.value})}
                    className="input"
                  >
                    <option value="">No assignment</option>
                    {organizationalUnits.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name} ({unit.level_name})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={createTenantUser}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
                <button
                  onClick={() => setShowCreateUserForm(false)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Users List */}
          <div>
            {tenantUsers.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                No users found in your organisation
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Name</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Username</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Email</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Role</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Organisational Unit</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Last Login</th>
                      <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantUsers.map((user) => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '0.75rem' }}>{user.full_name}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <code style={{ backgroundColor: '#f8f9fa', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                            {user.username}
                          </code>
                        </td>
                        <td style={{ padding: '0.75rem' }}>{user.email}</td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            backgroundColor: user.role === 'ADMIN' ? '#dc3545' : user.role === 'PROCESS_OWNER' ? '#fd7e14' : '#28a745',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {user.role.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          {user.organizational_unit_name || (
                            <span style={{ color: '#6b7280', fontStyle: 'italic' }}>Unassigned</span>
                          )}
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{
                            backgroundColor: user.is_active ? '#10b981' : '#ef4444',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
                          {user.last_login_at 
                            ? new Date(user.last_login_at).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          <button
                            onClick={() => setSelectedUserForEdit(user)}
                            className="btn btn-ghost btn-sm"
                            style={{ marginRight: '0.25rem' }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Edit User Modal */}
        {selectedUserForEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}>
              <h3 style={{ marginBottom: '1.5rem' }}>Edit User: {selectedUserForEdit.full_name}</h3>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  defaultValue={selectedUserForEdit.email}
                  onChange={(e) => setSelectedUserForEdit({...selectedUserForEdit, email: e.target.value})}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  defaultValue={selectedUserForEdit.full_name}
                  onChange={(e) => setSelectedUserForEdit({...selectedUserForEdit, full_name: e.target.value})}
                  className="input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select
                  defaultValue={selectedUserForEdit.role}
                  onChange={(e) => setSelectedUserForEdit({...selectedUserForEdit, role: e.target.value})}
                  className="input"
                >
                  <option value="EVALUATION_PANEL_MEMBER">Evaluation Panel Member</option>
                  <option value="PROCESS_OWNER">Process Owner</option>
                  <option value="ADMIN">Administrator</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Organisational Unit</label>
                <select
                  defaultValue={selectedUserForEdit.organizational_unit_id || ''}
                  onChange={(e) => setSelectedUserForEdit({...selectedUserForEdit, organizational_unit_id: e.target.value ? parseInt(e.target.value) : undefined})}
                  className="input"
                >
                  <option value="">No assignment</option>
                  {organizationalUnits.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.level_name})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  defaultValue={selectedUserForEdit.is_active.toString()}
                  onChange={(e) => setSelectedUserForEdit({...selectedUserForEdit, is_active: e.target.value === 'true'})}
                  className="input"
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
                <button
                  onClick={() => updateTenantUser(selectedUserForEdit.id, {
                    email: selectedUserForEdit.email,
                    full_name: selectedUserForEdit.full_name,
                    role: selectedUserForEdit.role,
                    organizational_unit_id: selectedUserForEdit.organizational_unit_id,
                    is_active: selectedUserForEdit.is_active
                  })}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Updating...' : 'Update User'}
                </button>
                <button
                  onClick={() => setSelectedUserForEdit(null)}
                  className="btn btn-ghost"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen density-compact">
      <main style={styles.contentWrapper}>
        <div className="flex justify-between items-center mb-xl">
          <div>
            <h1 className="page-title">Organisation Management</h1>
            <p className="page-subtitle">Configure your organisation's branding, structure, and user management</p>
          </div>
          <button 
            onClick={onBack} 
            className="btn btn-secondary btn-sm"
          >
            ← Back to Dashboard
          </button>
        </div>

        <div className="flex gap-sm mb-xl">
          <button
            className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`btn ${activeTab === 'branding' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setActiveTab('branding')}
          >
            🎨 Branding & Logo
          </button>
          <button
            className={`btn ${activeTab === 'structure' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setActiveTab('structure')}
          >
            🏢 Organisational Structure
          </button>
          <button
            className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
            onClick={() => setActiveTab('users')}
          >
            👥 User Management
          </button>
        </div>

        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'branding' && renderBrandingTab()}
        {activeTab === 'structure' && renderStructureTab()}
        {activeTab === 'users' && renderUsersTab()}

        {notification && (
          <div style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1000,
            background: notification.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}>
            {notification.message}
          </div>
        )}
      </main>
    </div>
  );
};

export default TenancyAdminDashboard;